import React, {Component} from 'react';
import {connect} from 'react-redux';
import {
    bindActionCreators,
    Dispatch
} from 'redux';
import memoize from 'memoize-one';
import _ from 'lodash';

import {Button} from 'primereact/button';
import {Dialog} from 'primereact/dialog';
import {Toast} from 'primereact/toast';

import User from '~/shared/src/model/User';
import {__} from '~/shared/src/utilities/messageCatalog';
import Joi from '~/shared/src/utilities/joiExtended';
import StackError from '~/shared/src/base/StackError';
import LocalizableError from '~/shared/src/base/LocalizableError';
import metaModel from '~/shared/src/utilities/metaModel';
import {isPropertyValueType} from '~/shared/src/mm/PropertySet';
import objectUtils from '~/shared/src/utilities/objectUtils';
import {BaseRequestParameters} from '~/shared/src/utilities/modules/common';
import {LoadMetaDataRowData} from '~/shared/src/utilities/modules/overview';
import {getSearchColumnFieldNames} from '~/shared/src/utilities/modules/finderOverview';
import {DataStructure} from '~/shared/src/utilities/dataStructure';
import {requestBaseParametersSchema} from '~/shared/src/model/BaseRequest';
import {
    getItemTypeNameColumnDataDescriptor,
    isHistorySearch,
    LoadFinderOverviewDataParameters
} from '~/shared/src/utilities/modules/finder';
import {panelSchema} from '~/shared/src/validationSchemes/panel';
import {
    ColumnDefinition,
    ColumnFilter,
    overviewSchema
} from '~/shared/src/validationSchemes/modules/overview';
import {
    itemTypeNameSchema,
    legendSchema
} from '~/shared/src/validationSchemes/modules/finder';
import {
    convertValueForType
} from '~/shared/src/utilities/valueConverters';
import {formatTimestamp} from '~/shared/src/utilities/valueFormaters';
import {getCustomFilters} from '~/shared/src/utilities/filterUtils';

import Toolbar, {
    ComponentProps as ToolbarComponentProps,
    componentProps as toolbarComponentProps
} from '~/frontend/src/components/base/ItemFinder/overview/Toolbar';
import DataTable, {
    defaultProps as dataTableDefaultProps,
    Props as DataTableProps,
    ComponentProps as DataTableComponentProps,
    componentProps as dataTableComponentProps,
    AllProps as DataTableAllProps,
    CellEditorData as DataTableCellEditorData,
    cellEditorDataKeys as dataTableCellEditorDataKeys
} from '~/frontend/src/components/base/DataTable/index';
import {
    getItemFinderLegendbar
} from '~/frontend/src/components/base/ItemFinder/common/utilities';

import {
    AppDispatch,
    StoreState,
    Thunk
} from '~/frontend/src/store';
import {getItemTypeDisplayName} from '~/frontend/src/utilities/renderHelpers';
import {
    preInitialize,
    initialize,
    extractViewPropsFromProps
} from '~/frontend/src/components/base/ItemFinder/overview/utilities';
import {isDataModified} from '~/frontend/src/utilities/dataUtils';
import {getColumnHeader} from '~/frontend/src/components/base/DataTable/utilities';
import {renderValue} from '~/frontend/src/utilities/valueRenderers';

import setPanelInfo, {SetPanelInfo} from '~/frontend/src/actions/panelManager/setPanelInfo';
import setPanelDirty, {SetPanelDirty} from '~/frontend/src/actions/panelManager/setPanelDirty';
import setPanelInvalid, {SetPanelInvalid} from '~/frontend/src/actions/panelManager/setPanelInvalid';
import closePanel from '~/frontend/src/actions/panelManager/closePanel';
import hideConfirmCloseDialog from '~/frontend/src/actions/panelManager/hideConfirmCloseDialog';
import setParam, {SetParamAction} from '~/frontend/src/actions/panel/common/setParam';
import unsetParam, {UnsetParamAction} from '~/frontend/src/actions/panel/common/unsetParam';
import loadData, {LoadData} from '~/frontend/src/actions/data/loadData';
import saveData from '~/frontend/src/actions/panel/dataTable/saveData';
import {SaveDataResult} from '~/shared/src/utilities/modules/data';
import {
    setElusiveData,
    unsetElusiveData,
    SetElusiveDataAction,
    UnsetElusiveDataAction
} from '~/frontend/src/actions/elusiveData';
import {PanelElusiveDataState} from '~/frontend/src/reducers/elusiveData';
import {
    MsgParams,
    unsetMessage,
    UnsetMessage
} from '~/frontend/src/actions/intercom';
import {PanelIntercomState} from '~/frontend/src/reducers/intercom';
import cancelRequests, {CancelRequests} from '~/frontend/src/actions/ajax/cancelRequests';
import {XhrComponentState} from '~/frontend/src/reducers/xhr';


const Actions = Object.assign( {}, { setPanelInfo, setPanelDirty, setPanelInvalid, setParam, unsetParam, loadData, setElusiveData, unsetElusiveData, unsetMessage, cancelRequests } ) as any;
type SetParam = <K extends keyof ComponentProps>( panelKey : string, paramKey : K, paramValue : ComponentProps[K] ) => SetParamAction;
type UnsetParam = ( panelKey : string, paramKey : keyof ComponentProps ) => UnsetParamAction;
type SetElusiveData = ( panelKey : string, data : PanelElusiveData ) => SetElusiveDataAction;
type UnsetElusiveData = ( panelKey : string, data : PanelElusiveData ) => UnsetElusiveDataAction;


export const defaultProps = {
    ...dataTableDefaultProps,

    /**
     * The ItemTypeName to show items for.
     */
    itemTypeName: '' as string,

    /**
     * Show the legend
     */
    legend: false as boolean,

    /**
     * Is the overview is editable.
     */
    editable: true as boolean
};

export type DefaultProps = typeof defaultProps;

type Props = DataTableProps & {

    /**
     * The transformer for the panels (component) state.
     * NOTE: Have to be set (in opposition to be a default) to be included in the state anyway!
     */
    panelTransformer? : string;

    /**
     * The title to display the panel with. If not set the title will be computed.
     */
    title? : string;

    /**
     * The icon to display the panel with. If not set the title will be computed.
     */
    iconCls? : string;
};

type PanelElusiveData = PanelElusiveDataState & DataTableCellEditorData & {
    showCouldNotSavePopup? : boolean
};

type StateProps = {

    /**
     * The running requests belonging to this component (uid).
     *
     * Will be mapped from global state.
     */
    xhrRequestsForComponent? : XhrComponentState;

    /**
     * The intercom messages for this component.
     *
     * Will be mapped from global state.
     */
    intercomMessages? : PanelIntercomState;

    /**
     * The elusive data (not persisted by redux store) for this component.
     *
     * Will be mapped from global state.
     */
    elusiveData? : PanelElusiveData;

    /**
     * The current user.
     *
     * Will be mapped from global state.
     */
    user? : User;
};

/**
 * Function props mapped by mapDispatchToProps
 */
type ActionPropsRaw = {
    // panel manager actions
    setPanelInfo : SetPanelInfo;
    setPanelDirty : SetPanelDirty;
    setPanelInvalid : SetPanelInvalid;

    // common panel actions
    setParam : SetParam;
    unsetParam : UnsetParam;

    // data actions
    loadData : LoadData;

    // elusiveData actions
    setElusiveData : SetElusiveData;
    unsetElusiveData : UnsetElusiveData;

    // intercom actions
    unsetMessage : UnsetMessage;

    // xhr actions
    cancelRequests : CancelRequests;
};
type ReplaceReturnType<T extends ( ...a : any ) => any, TNewReturn> = ( ...a : Parameters<T> ) => TNewReturn;
type ActionProps = {
    [K in keyof ActionPropsRaw] : ReplaceReturnType<ActionPropsRaw[K], ReturnType<ActionPropsRaw[K]> extends Thunk<any> ? ReturnType<ReturnType<ActionPropsRaw[K]>> : void>;
};

export type ComponentProps = Partial<DefaultProps> & Props;
type AllProps = DefaultProps & Props & StateProps & ActionProps;

type State = {
    dataTableKey : string;
    errorCount : number;
    errorMessage? : string | JSX.Element | ( string | JSX.Element )[];
    errorTooltip? : string | JSX.Element | ( string | JSX.Element )[];
    isDirty : boolean;
    lastItemTypeName : string;
    lastRowData : any;
};


/**
 * Finder overview
 */
class FinderOverview extends Component<AllProps, State> {

    static defaultProps = defaultProps;

    static searchRequestUid = 'loadOverviewData' as string;

    static viewSelectorModule = 'FinderOverview';

    /**
     * YOONITE lifecycle method
     */
    static onPreInitializePanel( props : ComponentProps & { user : User } ) : ComponentProps {
        if ( !props.schema && props.user?.application?.schema ) {
            props.schema = props.user.application.schema;
        }

        return preInitialize( {
            ...props
        } );
    }

    /**
     * YOONITE lifecycle method
     */
    static onInitializePanel( props : ComponentProps & { user : User } ) : ComponentProps {
        if ( !props.schema && props.user?.application?.schema ) {
            props.schema = props.user.application.schema;
        }

        return initialize( {
            ...props
        } );
    }

    /**
     * YOONITE lifecycle method
     */
    static onClosePanelAction( props : ComponentProps ) {
        return async ( dispatch : AppDispatch, getState : () => StoreState ) => {
            // abort running request
            dispatch( cancelRequests( props.uid ) );

            // clear global data
            delete global.data[ props.dataContext ];
        }
    }

    /**
     * YOONITE lifecycle method
     */
    static onSavePanelAction( props : ComponentProps ) {
        return async ( dispatch : AppDispatch, getState : () => StoreState ) => {
            await FinderOverview.savePanelData(
                dispatch,
                getState,
                props
            );
        }
    }

    /**
     * YOONITE lifecycle method
     */
    static onSaveAndClosePanelAction( props : ComponentProps ) {
        return async ( dispatch : AppDispatch, getState : () => StoreState ) => {
            const result = await FinderOverview.savePanelData(
                dispatch,
                getState,
                props,
                [
                    props.uid,
                    'notification',
                    'saveSuccess',
                    'closePanel'
                ]
            );

            // no save error -> close panel
            if ( result !== false && result.success ) {
                await dispatch( closePanel( props.uid ) );
            }
        }
    }

    /**
     * YOONITE lifecycle method
     */
    static async savePanelData( dispatch : AppDispatch, getState : () => StoreState, props : ComponentProps, successMsgParams : MsgParams | undefined = undefined ) : Promise<SaveDataResult | false> {
        const propsWithDefaults : DefaultProps & Props = {
                  ...defaultProps,
                  ...props
              },
              {
                  uid,
                  schema
              }                                        = propsWithDefaults,
              errorData                                = getState()?.elusiveData.errorData;

        // panel has errors -> show popup
        if ( errorData?.count ) {
            dispatch( hideConfirmCloseDialog( uid ) );
            dispatch( setElusiveData( uid, { showCouldNotSavePopup: true } ) );

            return Promise.resolve( false );
        }

        // panel has no errors -> save
        else {
            return dispatch( saveData(
                uid,
                schema,
                successMsgParams
            ) );
        }
    }

    /**
     * YOONITE lifecycle method
     */
    static buildPanelPathFromProps( props : AllProps ) : string {
        const parameters : string[] = [];

        let retVal = `#${FinderOverview.panelType}/${props.schema}`;

        if ( props.itemTypeName ) {
            const itemTypeLabel = metaModel[ props.schema ]?.getItemType( props.itemTypeName )?.label;

            if ( itemTypeLabel ) {
                retVal += `/${itemTypeLabel}`;
            }
        }

        if ( props.search ) {
            parameters.push( 'search' );
        }

        if ( props.searchString ) {
            parameters.push( `searchString=${props.searchString}` );
        }

        if ( parameters.length ) {
            retVal += `?${parameters.join( '&' )}`;
        }

        return retVal;
    }

    /**
     * YOONITE lifecycle method
     */
    static async buildPanelPropsFromPath( path : string ) : Promise<Partial<ComponentProps>> {
        const retVal : Partial<ComponentProps> = {},
              parts                            = path.match( /#([^?]*)(\?(.*))?/ ),
              params                           = parts?.[ 1 ]?.split( '/' ) ?? [],
              props                            = parts?.[ 3 ]?.split( '&' ) ?? [],
              stackError                       = new StackError( 'YN.base.resolvePanelPropsFromURL.error.title', { path: path } );

        // params[ 0 ] = panelType

        // params[ 1 ] = schema
        if ( params?.[ 1 ] ) {
            retVal.schema = params[ 1 ] as Schema;
        }
        else {
            stackError.add( new LocalizableError( 'YN.base.finder.resolvePanelPropsFromURL.error.couldNotResolveSchema' ) );
        }

        // params[ 2 ] = itemType.label
        if ( params?.[ 2 ] ) {
            if ( retVal.schema ) {
                const itemTypeName = metaModel[ retVal.schema ].getItemTypes().find( iT => iT.label === params[ 2 ] )?.name;

                if ( itemTypeName ) {
                    retVal.itemTypeName = itemTypeName;
                }
                else {
                    stackError.add( new LocalizableError( 'YN.base.finder.resolvePanelPropsFromURL.error.couldNotResolveItemTypeName', { itemTypeLabel: params[ 2 ] } ) );
                }
            }
        }

        if ( stackError.hasErrors() ) {
            throw ( stackError.toString() );
        }

        for ( const prop of props ) {
            const propParts = prop.split( '=' );

            if ( propParts[ 0 ] === 'search' ) {
                retVal.search = true;
            }

            if ( propParts[ 0 ] === 'searchString' ) {
                retVal.searchString = propParts[ 1 ];
            }
        }

        return retVal;
    }

    // panel manager config
    static panelType = 'FinderOverview';
    static panelTabTitle = 'Finder';
    static panelTabIcon = 'pi pi-search';

    static panelPropsSchemaMsgIdPrefix = 'YN.base.finder';
    static panelPropsSchema = Joi.object()
                                 .concat( panelSchema )
                                 .concat( Joi.object( {
                                     // panelSchema defaults
                                     panelType    : Joi.string().valid( FinderOverview.panelType ),
                                     panelTabTitle: Joi.string().default( FinderOverview.panelTabTitle ),
                                     panelTabIcon : Joi.string().default( FinderOverview.panelTabIcon )
                                 } ) )
                                 .concat( requestBaseParametersSchema )
                                 .concat( overviewSchema )
                                 .concat( Joi.object( {
                                     editable    : Joi.boolean().default( true ),
                                     itemTypeName: itemTypeNameSchema,
                                     legend      : legendSchema
                                 } ) );


    /* properties *************************************************************************************************************************/

    toast? : Toast | null;
    toastTopOffset = 55 as number;
    toastBottomOffset : number = 32;


    /**
     * @param props
     */
    constructor( props : AllProps ) {
        super( props );

        this.state = {
            dataTableKey    : `${props.uid}_dataTable${!props.itemTypeName ? `_${Math.random()}` : ''}`,
            errorCount      : 0,
            isDirty         : false,
            lastItemTypeName: props.itemTypeName,
            lastRowData     : undefined
        };

        this.checkForIntercomMessages = memoize( this.checkForIntercomMessages );
        this.setPanelInfo = memoize( this.setPanelInfo );
        this.getItemFinderLegendbar = memoize( this.getItemFinderLegendbar );
        this.getSearchColumnFieldNames = memoize( this.getSearchColumnFieldNames );

        this.rowGroupHeaderTemplate = this.rowGroupHeaderTemplate.bind( this );

        this.loadData = this.loadData.bind( this );
    }

    /**
     * REDUX stuff
     */
    static getDerivedStateFromProps( props : AllProps, state : State ) : Partial<State> {
        const {
                  dataContext,
                  schema,
                  elusiveData
              }                       = props,
              retVal : Partial<State> = {
                  dataTableKey    : state.dataTableKey,
                  errorCount      : 0,
                  errorMessage    : undefined,
                  errorTooltip    : undefined,
                  isDirty         : isDataModified( dataContext, schema ),
                  lastItemTypeName: props.itemTypeName,
                  lastRowData     : props.elusiveData?.metaData?.rowData
              };

        // itemTypeName or value change
        // BUGFIX: dynamic grouping not working - added Math.random to force really re-creating the DataTable every time value change
        if ( retVal.lastItemTypeName !== state.lastItemTypeName || retVal.lastRowData !== state.lastRowData ) {
            retVal.dataTableKey = `${props.uid}_dataTable${!props.itemTypeName ? `_${Math.random()}` : ''}`;
        }

        // errors
        if ( elusiveData?.errorData ) {
            const errorData = elusiveData.errorData;

            retVal.errorCount = errorData.count;

            retVal.errorTooltip = <div key="error" className="yn-tooltip-error">
                <div className="yn-tooltip-header">
                    {__( 'YN.base.frontend.dataTable.tooltip.error.title' )}
                </div>
                {errorData.tooltip}
            </div>;

            retVal.errorMessage = <>
                <div className="yn-error-message">
                    {__( 'YN.base.frontend.dataTable.couldNotSaveWhileErrors.text' )}
                </div>
                <hr className="yn-error-ruler"/>
                <div className="yn-error-suberror">
                    {errorData.message}
                </div>
            </>;
        }

        return retVal;
    }

    /**
     * REACT lifecycle method
     */
    componentDidMount() {
        const dataTable           = document.getElementById( `${this.props.uid}_datatable` ) as HTMLElement,
              dataTableHeader     = dataTable
                  ? dataTable.querySelector( `.p-datatable-scrollable-header` ) as HTMLElement
                  : null,
              toastTopOffset      = dataTableHeader?.offsetHeight ?? 0,
              dataTablePagination = dataTable
                  ? dataTable.querySelector( '.p-paginator' ) as HTMLElement
                  : null,
              toastBottomOffset   = dataTablePagination?.offsetHeight ?? 0;

        if ( toastTopOffset ) {
            this.toastTopOffset = toastTopOffset;
        }
        if ( toastBottomOffset ) {
            this.toastBottomOffset = toastBottomOffset;
        }

        this.afterRender();
    }

    /**
     * REACT lifecycle method
     */
    componentDidUpdate( prevProps : AllProps ) {
        this.afterRender();
    }

    /**
     * REACT lifecycle method
     */
    componentWillUnmount() {
        // abort running request
        this.props.cancelRequests( this.props.uid );
    }

    /**
     * REDUX stuff
     */
    shouldComponentUpdate( nextProps : AllProps, nextState : State ) : boolean {
        const changedProps = objectUtils.getDiffKeys(
                  this.props,
                  nextProps,
                  [
                      'actionColumns',
                      'cellEditorErrors',
                      'columns',
                      'columnDefaults',
                      'columnDefinitions',
                      'dataStructure',
                      'elusiveData',
                      'elusiveData.cellEditorErrors',
                      'elusiveData.cellEditorInitialValues',
                      'elusiveData.cellEditorValues',
                      'elusiveData.columnsCache',
                      'elusiveData.data',
                      'elusiveData.editCell',
                      'elusiveData.lastParams',
                      'elusiveData.metaData',
                      'filters',
                      'fixFilters',
                      'footer',
                      'rowClass',
                      'rowExpansionTemplate',
                      'rowGroupHeaderTemplate',
                      'rowGroupFooterTemplate',
                      'rowState',
                      'rowStyling',
                      'searchColumns',
                      'sorters',
                      'user',
                      'xhrRequestsForComponent',
                      //
                      'intercomMessages'
                  ],
                  [ 'elusiveData' ]
              ),
              stateChanged = this.state !== nextState;

        let retVal : boolean = false;

        if (
            stateChanged
            || changedProps.length === 1
            || changedProps.length > 2
            || ( changedProps.length === 2 && !changedProps.includes( 'elusiveData.lastParams' ) && !changedProps.includes( 'elusiveData.columnsCache' ) )
        ) {
            retVal = true;
        }

        return retVal;
    }


    /* event handlers *********************************************************************************************************************/

    /**
     * Handler for dataTable rowGroupHeaderTemplate call
     * @param rowData
     */
    rowGroupHeaderTemplate( rowData : LoadMetaDataRowData ) : JSX.Element {
        const {
                  schema,
                  rawMode,
                  showAdditionalRawNames
              }             = this.props,
              itemTypeId    = rowData.rowData[ 'itemType' ],
              itemType      = metaModel[ schema ].getItemType( itemTypeId ),
              itemTypeLabel = getItemTypeDisplayName( itemType?.id ?? 0, schema, rawMode, showAdditionalRawNames ),
              itemTypeIcon  = itemType?.icon;

        return <>
            <span className={`p-rowgroup-header-name-icon ${itemTypeIcon}`}>&nbsp;</span>
            {itemTypeLabel}
        </>;
    }

    /* private ****************************************************************************************************************************/

    afterRender() {
        this.checkForIntercomMessages( this.props.intercomMessages );

        this.setPanelInfo(
            this.props.title,
            this.props.iconCls,
            this.props.schema,
            this.props.status,
            this.props.timezone,
            this.props.timestamp,
            this.props.itemTypeName,
            this.props.dataStructure,
            this.props.columnDefinitions,
            this.props.rawMode,
            this.props.showAdditionalRawNames,
            this.props.elusiveData?.lastParams as LoadFinderOverviewDataParameters | undefined,
            this.state.isDirty,
            this.state.errorCount,
            FinderOverview.buildPanelPathFromProps( this.props )
        );
    }

    /**
     * Checks if there are intercom messages present and shows them if.
     *
     * @private
     */
    checkForIntercomMessages( intercomMessages? : PanelIntercomState ) {
        // check for toasts
        if ( this.toast && intercomMessages?.toast ) {
            const toastKeys = Object.keys( intercomMessages.toast );
            for ( const key of toastKeys ) {
                // show toast
                if ( intercomMessages.toast[ key ] ) {
                    this.toast.show( intercomMessages.toast[ key ] );
                }

                // clear toast message
                this.props.unsetMessage( this.props.uid, 'toast', key as string );
            }
        }
    }

    getLoadDataParameters( props? : DataTableAllProps ) : BaseRequestParameters & LoadFinderOverviewDataParameters {
        const {
                  status,
                  timestamp,
                  itemTypeName
              } = this.props,
              {
                  schema,
                  timezone,
                  //
                  searchString,
                  columns,
                  sorters,
                  page,
                  rows
              } = props ?? this.props;

        return {
            schema   : schema,
            status   : status,
            timestamp: timestamp,
            timezone : timezone,

            itemTypeName: itemTypeName,
            searchString: searchString,

            columns: columns.map( column => column.field ),
            filters: this.getApplyableFilters( props ),
            sorters: sorters,

            start: ( page - 1 ) * rows,
            limit: rows
        };
    }

    getApplyableFilters( props? : DataTableAllProps ) : ColumnFilter[] {
        const retVal : ColumnFilter[] = [],
              {
                  filters,
                  fixFilters,
                  elusiveData
              }                       = props ?? this.props,
              filtersToUse            = elusiveData?.newFilters ?? filters;

        // filter out empty filters
        if ( fixFilters ) {
            for ( let i = fixFilters.length - 1; i >= 0; i-- ) {
                if ( !!fixFilters[ i ].value || fixFilters[ i ].operator === 'empty' || fixFilters[ i ].operator === 'notEmpty' ) {
                    retVal.push( fixFilters[ i ] );
                }
            }
        }
        if ( filtersToUse ) {
            for ( let i = filtersToUse.length - 1; i >= 0; i-- ) {
                if (
                    !retVal.find( filter => filter.field === filtersToUse[ i ].field )
                    && ( !!filtersToUse[ i ].value || filtersToUse[ i ].operator === 'empty' || filtersToUse[ i ].operator === 'notEmpty' )
                ) {
                    retVal.push( filtersToUse[ i ] );
                }
            }
        }

        return retVal;
    }

    /**
     * load the overview data from the backend
     *
     * @param {Object} props
     *
     * @return {boolean}
     *
     * @private
     */
    loadData( props : DataTableAllProps ) : boolean {
        const {
                  uid,
                  dataContext,
                  elusiveData
              }          = this.props,
              {
                  schema
              }          = props,
              lastParams = elusiveData?.lastParams ?? '',
              params     = this.getLoadDataParameters( props );

        let retVal : boolean = false;

        // itemType selected or searchString entered
        // eslint-disable-next-line @typescript-eslint/prefer-nullish-coalescing
        if ( params.itemTypeName || params.searchString || params.filters?.length ) {

            retVal = true;

            // search parameters or page changed
            if ( lastParams !== 'requested' && ( JSON.stringify( params ) !== JSON.stringify( lastParams ) || !global.data[ dataContext ] ) ) {

                // clear expandedRows
                this.props.unsetParam( uid, 'expandedRows' );

                // clear cell edit data
                this.props.unsetElusiveData( uid, dataTableCellEditorDataKeys );

                const doLoad = async () => {
                    try {
                        await this.props.loadData(
                            'finder/loadFinderOverviewData',
                            dataContext,
                            uid,
                            FinderOverview.searchRequestUid,
                            schema,
                            {
                                params,
                                extraMetaDataKeys: [ 'rowData', 'rowDataMapping', 'total' ]
                            },
                            true
                        );
                    }
                    catch ( e ) {

                        // TODO: implement proper error message/handling

                        console.error( e );
                    }
                };

                void doLoad();
            }
        }

        return retVal;
    }

    /**
     * Sets the title and icon for the tab of this component.
     *
     * @private
     */
    setPanelInfo( panelTitle : string | undefined, panelIconCls : string | undefined, schema : Schema, status : Status, timezone : string, timestamp : string, itemTypeName : string, dataStructure : DataStructure, columnDefinitions : ColumnDefinition[], rawMode : boolean, showAdditionalRawNames : boolean, lastParams : LoadFinderOverviewDataParameters | undefined, isDirty : boolean, errorCount : number, path : string ) {
        const searchString = lastParams?.searchString,
              filters      = lastParams?.filters ?? [];

        let title            = __( panelTitle ?? FinderOverview.panelTabTitle ),
            icon             = panelIconCls ?? FinderOverview.panelTabIcon,
            tooltip : string = '';

        if ( itemTypeName && itemTypeName !== '' ) {
            const itemTypeLabel = getItemTypeDisplayName( itemTypeName, schema, rawMode, showAdditionalRawNames ),
                  itemTypeIcon  = metaModel[ schema ].getItemType( itemTypeName )?.icon;

            if ( !panelTitle ) {
                title = itemTypeLabel;
            }

            if ( !panelIconCls && itemTypeIcon ) {
                icon = `yn-tab-image-icon icon-itemtype-overview ${itemTypeIcon}`;
            }

            tooltip += `<div className="yn-tooltip-header">${__( 'YN.base.finder.overview.tooltip.title', { itemTypeLabel } )}</div>`;
        }
        else {
            tooltip += `<div className="yn-tooltip-header">${__( 'YN.base.finder.overview.tooltip.titleWithoutItemType' )}</div>`;
        }

        // error
        if ( errorCount ) {
            tooltip += `<div className="yn-tooltip-info mb-0">
                            <div className="yn-tooltip-info-title"><span className="fas fa-exclamation-circle yn-error mr-2"></span> ${__( 'YN.base.panel.tooltip.hasError' )}</div>
                        </div>`;
        }

        // dirty
        else if ( isDirty ) {
            tooltip += `<div className="yn-tooltip-info mb-0">
                            <div className="yn-tooltip-info-title"><span className="fas fa-exclamation-triangle yn-dirty mr-2"></span> ${__( 'YN.base.panel.tooltip.isDirty' )}</div>
                        </div>`;
        }

        // settings
        tooltip += `<div className="yn-tooltip-info">`;
        tooltip += `<div className="yn-tooltip-info-row">`;
        // schema
        tooltip += `<div className="yn-tooltip-info-content" style="width: 5rem;">
                                <div className="yn-tooltip-info-content-label">${__( 'YN.base.finder.schema.label' )}</div>
                                <div className="yn-tooltip-info-content-text">${schema}</div>
                            </div>`;
        // searchString
        if ( searchString ) {
            tooltip += `<div className="yn-tooltip-info-content flex-1">
                                <div className="yn-tooltip-info-content-label">${__( 'YN.base.finder.searchString.label' )}</div>
                                <div className="yn-tooltip-info-content-text">${searchString}</div>
                            </div>`;
        }
        tooltip += '</div>';
        tooltip += '</div>';

        // adv search options
        if ( status !== 'active' || timestamp !== 'now' ) {
            tooltip += `<div className="yn-tooltip-info">
                                <div className="yn-tooltip-info-title">${__( 'YN.base.finder.tooltip.advSearchOptions' )}</div>`;
        }
        // status
        if ( status !== 'active' ) {
            let statusLabel = '';

            switch ( status ) {
                case 'all-non-history':
                    statusLabel = __( 'YN.base.status.allNonHistory' );
                    break;
                case 'all':
                    statusLabel = __( 'YN.base.status.all' );
                    break;
            }

            tooltip += `<div className="yn-tooltip-info-content">
                                <div className="yn-tooltip-info-content-label">${__( 'YN.base.finder.status.label' )}</div>
                                <div className="yn-tooltip-info-content-text">${statusLabel}</div>
                            </div>`;
        }
        // timestamp
        if ( timestamp !== 'now' ) {
            tooltip += `<div className="yn-tooltip-info-content">
                                <div className="yn-tooltip-info-content-label">${__( 'YN.base.finder.timestamp.label' )}</div>
                                <div className="yn-tooltip-info-content-text">${formatTimestamp( timestamp, timezone )}</div>
                            </div>`;
        }
        if ( status !== 'active' || timestamp !== 'now' ) {
            tooltip += `</div>`;
        }

        // filters
        if ( filters.length ) {
            tooltip += `<div className="yn-tooltip-info">
                                <div className="yn-tooltip-info-title">${__( 'YN.base.finder.overview.tooltip.filters' )}</div>`;

            for ( const filter of filters ) {
                const columnDefinition = columnDefinitions.find( cD => cD.field === filter.field );

                if (
                    columnDefinition
                    && (
                        filter.operator === 'empty'
                        || filter.operator === 'notEmpty'
                        || (
                            filter.value !== undefined
                            && filter.value !== null
                            && filter.value !== ''
                        )
                    )
                ) {
                    const valueType     = filter.valueType ?? 'STRING',
                          valueRenderer = ( inValue : React.ReactNode ) => {
                              let outValue : React.ReactNode = inValue;

                              if ( typeof outValue === 'string' ) {
                                  const stringOutValue = outValue.trim();

                                  if (
                                      ( valueType === 'DATE' || valueType === 'TIME' || valueType === 'TIMESTAMP' )
                                      && stringOutValue.match( /^now(( (\+|\-) ([0-9]+) (seconds?|minutes?|hours?|days?|months?|years?))*)$/i )
                                  ) {
                                      const matches = stringOutValue.match( /^(now)(?:(?: (\+|\-) ([0-9]+) (seconds?|minutes?|hours?|days?|months?|years?))*)$/i );

                                      if ( matches?.length === 2 ) {
                                          outValue = __( `YN.base.${valueType}.now.label` );
                                      }
                                      else if ( matches?.length === 5 ) {
                                          matches[ 4 ] = matches[ 4 ].toLowerCase();

                                          if ( matches[ 4 ].toLowerCase().lastIndexOf( 's' ) === matches[ 4 ].length - 1 ) {
                                              matches[ 4 ] = matches[ 4 ].substring( 0, matches[ 4 ].length - 1 );
                                          }

                                          outValue = `${__( `YN.base.${valueType}.now.label` )} ${matches[ 2 ]} ${matches[ 3 ]} ${__( `YN.base.dateTime.${matches[ 3 ] === '1' ? matches[ 4 ] : `${matches[ 4 ]}s`}.label` )}`;
                                      }
                                  }
                                  else if ( isPropertyValueType( valueType ) ) {
                                      outValue = renderValue( valueType, convertValueForType( stringOutValue, valueType ) );
                                  }
                              }

                              return outValue;
                          };

                    let value : React.ReactNode = filter.value ?? ''

                    if ( filter.value === undefined || filter.value === null ) {
                        value = '';
                    }

                    if ( typeof value === 'string' && value.includes( '||' ) ) {
                        value = getCustomFilters( value, valueType )
                            .map( cF => `${cF.value ? `<span class="p-text-secondary">${__( `YN.base.operator.${cF.matchMode}` )}</span> ${valueRenderer( cF.value )}` : __( `YN.base.operator.${cF.matchMode}` )}`.trim() )
                            .join( ` <span class="p-text-secondary">${__( 'YN.base.operator.or' ).toUpperCase()}</span> ` );
                    }
                    if ( typeof value === 'string' && value.includes( '&&' ) ) {
                        value = getCustomFilters( value, valueType )
                            .map( cF => `${cF.value ? `<span class="p-text-secondary">${__( `YN.base.operator.${cF.matchMode}` )}</span> ${valueRenderer( cF.value )}` : __( `YN.base.operator.${cF.matchMode}` )}`.trim() )
                            .join( ` <span class="p-text-secondary">${__( 'YN.base.operator.and' ).toUpperCase()}</span> ` );
                    }
                    if ( typeof value === 'string' && filter.operator !== 'custom' && isPropertyValueType( valueType ) ) {
                        value = valueRenderer( value );
                    }

                    tooltip += `<div className="yn-tooltip-info-content flex-row">
                                <div className="yn-tooltip-info-content-label" style="width: 12rem; text-overflow: ellipsis;">${getColumnHeader( columnDefinition, dataStructure, schema, rawMode, showAdditionalRawNames )}</div>
                                <div className="yn-tooltip-info-content-text">${filter.operator !== 'custom' ? `<span class="p-text-secondary">${__( `YN.base.operator.${filter.operator}` )}</span> ` : ''}${value}</div>
                            </div>`;
                }
            }

            tooltip += '</div>';
        }

        this.props.setPanelInfo( this.props.uid, { title, icon, tooltip, dirty: isDirty, invalid: !!errorCount, path } );
    }

    /**
     * Memoizable wrapper.
     *
     * @private
     */
    getItemFinderLegendbar( uid : string, schema : Schema, rawMode : boolean, showAdditionalRawNames : boolean, legend : boolean ) : JSX.Element {
        return getItemFinderLegendbar( uid, schema, rawMode, showAdditionalRawNames, legend );
    }

    getSearchColumnFieldNames( dataStructure : DataStructure ) {
        return getSearchColumnFieldNames( dataStructure );
    }

    /**
     * REACT lifecycle method
     */
    render() {
        const {
                  uid,
                  schema,
                  timestamp,
                  itemTypeName,
                  dataStructure,
                  rawMode,
                  showAdditionalRawNames,
                  legend,
                  editMode,
                  editable,
                  expandedRows,
                  xhrRequestsForComponent,
                  elusiveData
              }                 = this.props,
              {
                  dataTableKey,
                  errorMessage
              }                 = this.state,
              historySearch     = isHistorySearch( timestamp ),
              toolbarProps      = {
                  ..._.pick( this.props, toolbarComponentProps ) as ToolbarComponentProps,

                  key: `${uid}_toolbar`,

                  editable              : editable && !historySearch,
                  editMode              : editMode && !historySearch,
                  showSaveButton        : editable,
                  saveButtonErrorCount  : this.state.errorCount,
                  saveButtonErrorTooltip: this.state.errorTooltip,
                  showAddButton         : editable,

                  exportLoadDataParameters: this.getLoadDataParameters( this.props as unknown as DataTableAllProps ),

                  viewSelectorPanelProps: { ...extractViewPropsFromProps( this.props ) }
              },
              valueFieldMapping = elusiveData?.metaData?.rowDataMapping ?? {};

        // search without ItemType -> group results by ItemType
        let value : LoadMetaDataRowData[]                               = ( elusiveData?.metaData?.rowData ?? [] ),
            additionalDataTableProps : Partial<DataTableComponentProps> = {};

        if ( !itemTypeName ) {
            const columnDataDescriptor = getItemTypeNameColumnDataDescriptor( dataStructure ),
                  field                = columnDataDescriptor.alias;

            value = value.map( rowData => {
                return {
                    ...rowData,
                    itemType: rowData[ field ]
                };
            } );

            additionalDataTableProps = {
                rowGroupMode          : 'subheader',
                groupRowsBy           : 'itemType',
                rowGroupHeaderTemplate: this.rowGroupHeaderTemplate,
                expandableRowGroups   : true
            };

            // expand all groups if not
            if ( !expandedRows ) {
                additionalDataTableProps.expandedRows = [];

                let lastItemType : string = '';
                for ( const val of value ) {
                    if ( val[ field ] !== lastItemType ) {
                        lastItemType = val[ field ];
                        additionalDataTableProps.expandedRows.push( { itemType: val.itemType } );
                    }
                }
            }
        }

        const dataTableProps : DataTableComponentProps & { key : string } = {
            ..._.pick( this.props, dataTableComponentProps ) as DataTableComponentProps,
            ...additionalDataTableProps,

            key: dataTableKey,

            editable: editable && !historySearch,
            editMode: editMode && !historySearch,

            loading          : !!xhrRequestsForComponent,
            value            : value,
            valueFieldMapping: valueFieldMapping,
            totalRecords     : elusiveData?.metaData?.total ?? 0,

            getApplyableFilters: this.getApplyableFilters,
            reload             : this.loadData,
            noMultiLineCells   : true,
            searchColumns      : this.getSearchColumnFieldNames( dataStructure ),
            footer             : this.getItemFinderLegendbar( uid, schema, rawMode, showAdditionalRawNames, legend ),

            className: `yn-finder${historySearch ? ' yn-finder-historysearch' : ''}`
        };

        return <>
            <Dialog
                key={`${uid}_dialog`}
                header={__( 'YN.base.frontend.dataTable.couldNotSaveWhileErrors.title' )}
                footer={<div><Button label="OK" className="p-button-outlined" onClick={() => this.props.setElusiveData( uid, { showCouldNotSavePopup: false } )}/></div>}
                maskClassName="yn-dialog-panel-modal"
                appendTo="self"
                position="top"
                visible={elusiveData?.showCouldNotSavePopup}
                style={{ width: 'auto', maxWidth: '80%' }}
                closable={false}
                className="yn-error-dialog"
                onHide={() => {
                }}>
                {errorMessage}
            </Dialog>
            <Toolbar
                {...toolbarProps}
            />
            <div style={{ position: 'relative' }} className="yn-flex-layout-parent">
                <Toast
                    ref={( el ) => this.toast = el}

                    key={`${uid}_toast`}

                    position="bottom-right"
                    style={{ position: 'absolute', marginTop: this.toastTopOffset, marginBottom: this.toastBottomOffset }}
                />
                <DataTable
                    {...dataTableProps}
                />
            </div>
        </>;
    }
}

/**
 * REDUX stuff
 */
function mapStateToProps( state : StoreState, ownProps : ComponentProps ) : StateProps {
    const retVal : StateProps = {
        xhrRequestsForComponent: undefined,
        intercomMessages       : undefined,
        elusiveData            : undefined,
        user                   : undefined
    };

    // get the running xhr request belonging to this component (uid)
    if ( state.xhr?.[ ownProps.uid ] ) {
        retVal.xhrRequestsForComponent = state.xhr[ ownProps.uid ];
    }

    // get intercom messages
    if ( state.intercom?.[ ownProps.uid ] ) {
        retVal.intercomMessages = state.intercom[ ownProps.uid ];
    }

    // get elusiveData
    // for the data (items and references) belonging to this component
    if ( state.elusiveData?.[ ownProps.dataContext ] ) {
        retVal.elusiveData = {
            ...retVal.elusiveData ?? {},
            ...state.elusiveData[ ownProps.dataContext ]
        };
    }
    // for all other information belonging to this component
    if ( state.elusiveData?.[ ownProps.uid ] ) {
        retVal.elusiveData = {
            ...retVal.elusiveData ?? {},
            ...state.elusiveData[ ownProps.uid ]
        };
    }

    // get some session user information
    if ( state.session?.user ) {
        retVal.user = state.session.user;
    }

    return retVal;
}

/**
 * REDUX stuff
 */
function mapDispatchToProps( dispatch : Dispatch ) {
    return bindActionCreators( Actions, dispatch );
}

export default connect( mapStateToProps, mapDispatchToProps )( FinderOverview );
